import { useState, useCallback, useMemo } from 'react';
import { QueryKey } from 'react-query';
import isArray from 'lodash/isArray';
import includes from 'lodash/includes';
import some from 'lodash/some';
import first from 'lodash/first';

import {
  UniversalFilterNumberValue,
  UniversalFilterNumberModes,
  UniversalFilterNumberFilterState,
  UniversalFilterNumberFilterOptions,
  UniversalFilterNumberOnFilterStateChange,
  UniversalFilterNumberOnChange
} from '../../UniversalFilterNumber.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterNumberFilterState } from '../useUniversalFilterNumberFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterNumberModes.IS;

const allModeOptions: UniversalFilterTemplateModeOption<UniversalFilterNumberModes>[] =
  [
    {
      value: UniversalFilterNumberModes.IS,
      i18nText: filtersKeys.modes.is
    },
    {
      value: UniversalFilterNumberModes.IS_NOT,
      i18nText: filtersKeys.modes.isNot
    },
    {
      value: UniversalFilterNumberModes.IS_EMPTY,
      i18nText: filtersKeys.modes.isEmpty
    },
    {
      value: UniversalFilterNumberModes.IS_NOT_EMPTY,
      i18nText: filtersKeys.modes.isNotEmpty
    },
    {
      value: UniversalFilterNumberModes.GREATER_THAN_EQUAL,
      i18nText: filtersKeys.modes.greaterThanEqual
    },
    {
      value: UniversalFilterNumberModes.LESS_THAN_EQUAL,
      i18nText: filtersKeys.modes.lessThanEqual
    }
  ];

interface UniversalFilterNumberOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterNumberValue;
  options?: UniversalFilterNumberFilterOptions;
  onChange: UniversalFilterNumberOnChange;
}

function useUniversalFilterNumber({
  filterStateCacheKey,
  filterValue,
  options,
  onChange
}: UniversalFilterNumberOptions) {
  const modeOptions = useMemo<
    UniversalFilterTemplateModeOption<UniversalFilterNumberModes>[]
  >(() => {
    if (!isArray(options?.availableModes)) {
      return allModeOptions;
    }

    return allModeOptions.filter((modeOption) =>
      includes(options.availableModes, modeOption.value)
    );
  }, [options]);

  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterNumberFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterNumberFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterNumberFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: some(modeOptions, (option) => option.value === defaultMode)
            ? defaultMode
            : first(modeOptions)?.value,
          value: null
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterNumberOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterNumberModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        value: filterState.value
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleValueChange = useCallback<(value?: number) => void>(
    (value) => {
      handleFilterStateChange({
        mode: filterState.mode,
        value: value
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleValueChange
  };
}

export default useUniversalFilterNumber;
